/**
 * @module Routes.Scheduling.Pricing.Modals.AddPricing.Section
 *
 * One folded card in the booking option modal.
 *
 * The form is long and most of it is set once and never looked at again, so
 * the designs fold it into cards: a title, a line of summary while it is shut,
 * and the fields inside it when it is open. The same shape the payments card
 * already had, made shareable so every card reads alike.
 */
import React from "react";

import { cls } from "app/utils";

import styles from "./section.module.scss";

type PropsDef = {
  title: string;
  /** What the card says about itself while it is folded away. */
  summary?: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  testId?: string;
};

export default function Section({
  title,
  summary,
  isOpen,
  onToggle,
  children,
  testId,
}: PropsDef) {
  return (
    <div className={styles.section}>
      <div
        className={styles.header}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
        data-testid={testId}
      >
        <div>
          <div className={styles.title}>{title}</div>
          {!isOpen && summary && (
            <div className={styles.summary}>{summary}</div>
          )}
        </div>
        <i className={cls("ico ico-chevron", { [styles.open]: isOpen })} />
      </div>

      {isOpen && <div className={styles.content}>{children}</div>}
    </div>
  );
}
